import selectn        from 'selectn'
import { common }     from './common'
import qs from 'query-string'
import StandingConfig from '../../../components/standings/config'
import { showBottomAdModule, getTrackingImg } from '../../utils/common'
import translate    from '../../utils/translate'

const UNDEFINED       = typeof void(0)
const TEAM_COL_KEY    = '__team__'

/* Helpers need to be moved to utils but for now until merged it will be kept here */
const helpers = {

  /**
  * Take in dates as strings and extract years to return year string
  *
  * @param  {Object}    edition     Edition config
  * @param  {Object}    params      Router params
  * @param  {Object}    location    Router location
  * @return {boolean}               Merged super object for ease of transportation
  **/
  requestObj: function ( edition = {}, params = {}, location = {} ) {

    return {
      edition : edition,
      params : params,
      location : location
    }
  },

  /**
  * Compares dates string to regex as a rudimentry check for a date value
  *
  * @param  {string}  date  Date ("2015-10-01T07:00Z")
  * @return   {boolean}           true/false
  **/
  isDateString: function ( date ) {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z$/.test(date)
  },

  /**
  * Take in dates as strings and extract years to return year string
  *
  * @param  {string}  date  Start date ("2015-10-01T07:00Z")
  * @param  {integer} size  2 or 4
  * @return {string}        Year as YYYY or YY
  **/
  getYearFromDateString: function ( date = '', size = 4 ) {
    if ( helpers.isDateString( date ) && (size !== 2 || size !== 4) ) {
      return date.substring( ( 4 - size ), 4 )
    }
    return ''
  },

  /**
  * Splits string on ' ' and joins with '+' to create a single key for tracking
  *
  * @param    {String}  description     Description of element or link
  * @return   {String}                  Join string
  **/
  descriptionToKey: function ( description ) {
    let res = description.replace( ' ', '+' )
    return res
  }
}

const standings = {

  /**
  * Process data from APIs
  *
  * @param  {Object}  standingRes     API response from standings API
  * @param  {Object}  leagueRes       API response from league API
  * @param  {Object}  edition         edition config
  * @param  {Object}  params          router params
  * @param  {Object}  location        router location
  * @param  {Object}  appProps        app Props
  * @return {Object}                  standings, seasons, current-season & league-title
  *
  **/
  processData: function (standingRes = {}, leagueRes = {}, edition = {}, params = {}, location = {}, appProps = {}) {

    const standingsData = selectn( 'data', standingRes )
    const leaguesData   = selectn( 'data.leagues', leagueRes ) || []
    const request       = helpers.requestObj( edition, params, location ) /* until such time as we have 1 var i will fake it */
    const hrefConfig    = { showSport : edition.isMultiSports }
    const query         = location.query || qs.parse(location.search)
    const currentLeague = standings.getLeagueName(standingsData)
    const leagueSlug = currentLeague.replace(/'/g, '')
      .replace(/[^A-Z0-9]+/ig, '-')
      .toLowerCase() || ''

    const siteType = appProps.siteType
    // Default return types
    let processedData = {
      seasons :         [],
      leagues :         [],
      currentSeason :   '',
      currentLeague :   '',
      leagueTitle :     '',
      glossaries :      [],
      rules :           '',
      meta :            {},
      analytics :       {},
      standingsTable :  {},
      notes :           [],
      typeOptions :     {},
      noTableText :     translate(edition, 'Table_not_yet_available') || '',
      adblock : selectn('_adblock', query) === 'true' ? true : false
    }

    // If response or params are empty, return default data
    if ( typeof standingsData === UNDEFINED || (params !== null && typeof params !== UNDEFINED && Object.keys( params ).length === 0) ) {
      return processedData
    }

    try {

      // get the config
      const config = StandingConfig.get({
        sport : params.sport,
        root :  params.sport,
        page :  params.pageType,
        params
      })

      const formattedData           = standings.formatData( request, config, standingsData, edition, hrefConfig ) || {}

      // Get list of valid seasons - those who have standings
      processedData.seasons         = standings.filterSeasons( request, standingsData, hrefConfig, edition )
      processedData.leagues         = standings.filterLeagues( request, leaguesData, hrefConfig, edition )

      // Get current season
      processedData.currentSeason   = formattedData.seasonText || params.season || '-'
      processedData.currentLeague   = currentLeague
      processedData.currentLeagueId = standings.getLeagueId( processedData.currentLeague, leaguesData )

      // get the league name
      processedData.leagueTitle     = standings.getLeagueTitle( processedData.currentLeague, processedData.currentSeason , params, edition )
      processedData.leagueTableLink = standings.getLeagueLink( params, processedData.leagues, processedData.currentLeague, params )
      processedData.rules           = standings.getRules( standingsData )
      processedData.meta            = standings.getMetaData( edition, processedData.leagueTitle, processedData.leagueTableLink, params, location )
      processedData.analytics       = standings.getAnalytics( processedData.meta, processedData.currentLeague, edition.isEspnCricinfo )
      processedData.standingsTable  = {
        data :    formattedData.data || [],
        header :  formattedData.header || {},
        columns : formattedData.columns || [],
        merged :  config.mergeTable || false
      }
      processedData.glossaries      = formattedData.glossary
      processedData.notes           = selectn( 'data.notes', formattedData ) || []
      processedData.typeOptions     = formattedData.typeOptions || {}
      processedData.showBtmAdModule = showBottomAdModule(params.pageType)
      processedData.siteType        = siteType
      processedData.trackingImg     = getTrackingImg(edition, params.pageType, processedData.currentLeague, leagueSlug, processedData.currentLeagueId, location.pathname, siteType, '', query, '')

    } catch ( e ) {
      console.log( e )
    }
    //console.log(processedData.seasons)
    return processedData
  },

  /**
  * Builds meta data object recognised by 'updateMeta' function
  *
  * @param  {Object}  edition           Edition doc
  * @param  {String}  leagueTitle       Current league name
  * @param  {String}  leagueTableLink   league table link url
  * @param  {Object}  params       route params
  * @return   {Object}                Object
  **/
  getMetaData: function ( edition = {}, leagueTitle = '', leagueTableLink = '' , params = {} ) {
    let sport = params.sport || ''
    let title = ''
    let description = ''
    let keywords = ''
    if ( sport === 'rugby' ) {
      let transKeyObjs = {
        title : {
          key : 'meta.title.rugby.standings',
          league : leagueTitle
        },
        keywords: {
          key : 'meta.keywords.rugby.standings',
          league : leagueTitle
        },
        description: {
          key : 'meta.description.rugby.standings',
          league : leagueTitle
        }
      }
      title = translate(edition, transKeyObjs.title) || ''
      description = translate(edition, transKeyObjs.description) || ''
      keywords = translate(edition, transKeyObjs.keywords) || ''
    }
    return {
      title :        title || `${leagueTitle} - ESPN`,
      description :  description || `Take a look at the latest ${leagueTitle}.`,
      keywords :     keywords || `${leagueTitle}, ${sport}`,
      website :      'article',
      canonical :    leagueTableLink,
      sport : sport && sport.toLowerCase()
    }
  },

  /**
  * Builds analytics data object recognised by 'analytics' function
  *
  * @param    {Object}  meta        Meta object returned from 'getMetaData'
  * @param {String} league league league
  * @param {Boolean} isEspnCricinfo check if it is cricinfo
  * @return   {Object}                Object
  **/
  getAnalytics: function ( meta, league, isEspnCricinfo) {
    let omniture = {}
    let metaLeague = league ? league.replace(/'/g, '')
      .replace(/[^A-Z0-9]+/ig, '-')
      .toLowerCase() : ''
    //override analytics only for cricinfo
    if (isEspnCricinfo && metaLeague !== '') {
      omniture = {
        pageName: `${metaLeague}:standings`,
        channel: `${metaLeague}`,
        eVar11: `cricinfo:${metaLeague}:standings`,
        prop5: `${metaLeague}:standings`,
        prop26: `${metaLeague}`,
        eVar21: `${metaLeague}`
      }
    }
    return {
      chartbeat: {
        title: meta.title
      },
      omniture
    }
  },

  /**
  * Function to itterate the standings API return value and calculate the headings
  * data values and other sundary elements the components need to build the standings page
  *
  * @param    {Object}  request     Request Object made from 'editions', 'location' and 'params'
  * @param    {Object}  config      Config values
  * @param    {Object}  data        Standings API return value (unmolested)
  * @param    {Object} edition      edition object
  * @param    {Object} hrefConfig   config object for create href function
  * @return   {Object}                Object
  **/
  formatData: function ( request = {}, config = {}, data = {}, edition, hrefConfig ) {

    const sport         = selectn( 'params.sport', request ) || ''
    const standingsData = standings.getFirstStandings( data ) || {}
    const validTypes    = standings.getColumnFilter( config, standingsData ) || {}
    const season        = standings.getStandingsSeason( config, standingsData ) || ''
    const seasonType    = standings.getStandingsSeasonType( config, standingsData ) || 0
    const seasonData    = standings.getCurrentSeasonData( season, data ) || {}
    const headerData    = standings.getHeader( request, config, validTypes, edition, hrefConfig ) || {}
    const typeOptions   = standings.getSeasonTypes( request, seasonData, seasonType, edition ) || {}
    let seasonText    = standings.formatSeason( selectn( 'startDate', seasonData ) || '', selectn( 'endDate', seasonData ) || '' ) || ''

    if (sport === 'rugby') {
      // don't use a year range for ruby seasons
      seasonText = (seasonData && seasonData.year) || ''
    }

    return {
      data :        standings.getChildren( request, config, validTypes, data, edition ) || {},
      header :  {
        data :      headerData.data,
        sortDir :   headerData.sortDir,
        sortCol :   headerData.sortCol
      },
      columns :     headerData.columns || [],
      glossary :    headerData.glossary || [],
      season :      season,
      seasonType :  seasonType,
      seasonText :  seasonText,
      typeOptions : typeOptions
    }

  },

  /**
  * Parent function to itterate the actual groups and standings. NOTE it uses recursion to itterate
  * through the children values. It will look for any children and itterate though them, if no
  * children it will look for standings to extract the entries from.
  *
  * @param    {Object}  request     Request Object made from 'editions', 'location' and 'params'
  * @param    {Object}  config      Config values
  * @param    {Object}  validTypes    Object returned from  'standings.getColumnFilter'
  * @param    {Object}  data        Standings API return value (unmolested)
  * @return   {Object}                Object of filtered data
  **/
  getChildren: function ( request = {} , config = {}, validTypes = {}, data = [], edition ) {

    let standingsData = {
      groups :        [],
      entries :       [],
      name :          standings.getLeagueName( data ),
      abbreviation :  standings.abbreviationMod( selectn('abbreviation', data) || '' ),
      notes :         []
    }

    if ( data.hasOwnProperty( 'children' ) ) {
      const childData = selectn( 'children', data ) || []
      const childLen  = childData.length || 0
      for (let i = 0; i < childLen; i++) {
        const groupData = standings.getChildren( request, config, validTypes, childData[i], edition )
        if ( groupData.groups.length || groupData.entries.length ) {
          if (groupData.name.toLowerCase() === standingsData.name.toLowerCase()) {
            groupData.name = groupData.abbreviation
          }
          standingsData.groups.push(groupData)
          standingsData.notes   = groupData.notes
        }
      }
    }else if ( data.hasOwnProperty('standings') ) {
      const entries = selectn('standings.entries', data) || []
      const entriesData = standings.getEntries( request, config, validTypes, entries, edition )
      standingsData.entries   = entriesData.entries
      standingsData.notes   = entriesData.notes
    }

    return standingsData
  },

  /**
  * Modifies any value matching regex to remove '/' and replace with '-'
  *
  * @param    {String}  abbrev      Looking for strings like '2015/16'
  * @return   {String}                Object of filtered data
  **/
  abbreviationMod: function ( abbrev = '' ) {
    if ( /^\d{4}\/\d{2}/.test(abbrev) ) {
      return abbrev.replace('/','-')
    }
    return abbrev
  },

  /**
  * Itterates the entries of a standings node to pull out the required stats and
  * team information for each row of the table.
  *
  * @param    {Object}  request     Request Object made from 'editions', 'location' and 'params'
  * @param    {Object}  config      Config values
  * @param    {Object}  validTypes    Object returned from 'standings.getColumnFilter'
  * @param    {Object}  data        Standings node of standings API
  * @return   {Object}                Object of filtered data
  **/
  getEntries: function ( request = {}, config = {}, validTypes = {}, data = [], edition ) {

    /* filter the entries */
    const entryLen    = data.length || 0
    const leagueId    = selectn( 'params.league', request ) || ''
    const sport       = selectn( 'params.sport', request ) || ''
    let isCollege     = false
    let entryData     = []
    let previousRank  = null
    let prevQualified = null
    let notesData     = []
    let lang = edition.language || 'en'

    if ( leagueId === 'ncf' || leagueId === 'ncb' || leagueId === 'ncw' ) {
      isCollege = true
    }

    for ( let i = 0; i < entryLen; i++ ) {
      const stats       = selectn( 'stats', data[i] ) || []
      const statLen     = stats.length || 0
      let isActive      = selectn( 'team.isActive', data[i] ) || false
      let hasSprite     = false
      let dataRoute     = false
      let entryVals     = {}

      /* HACK for cricket */
      //SEWEB-13325 hide team links for es/pt
      if( sport === 'cricket' ) {
        isActive = true
        dataRoute = true
      } else if ( sport === 'rugby-league' || (lang === 'es' || lang === 'pt')) {
        isActive = false /* HACK to hide links until API corrected */
      }

      for ( let x = 0; x < statLen; x++ ) {
        const key = selectn('type', stats[x]) || ''
        if ( validTypes[key] || key === 'rank' || key === 'seed' || key==='qualified') {
          entryVals[key] = selectn('displayValue', stats[x]) || ''
          if ( validTypes[key] && validTypes[key].colorize === true ) {
            if ( entryVals[key] > 0 && entryVals[key].charAt(0) !== '+' ) {
              entryVals[key] = '+'+entryVals[key]
            }
          }
        }
      }

      if ( isActive && ( leagueId === 'nba' || leagueId === 'mlb' || leagueId === 'nfl' || leagueId === 'nhl' ) ) {
        hasSprite = true
      }

      let displayName = ''
      if ( !config.useDisplayName ) {
        displayName = selectn( 'team.location', data[i] ) || ''
      } else {
        displayName = selectn( 'team.displayName', data[i] ) || ''
      }

      let seedValue = ''
      if ( config.showPlayoffSeed ) {
        if ( typeof entryVals.seed !== UNDEFINED && entryVals.seed ) {
          var seedNo = parseInt( entryVals.seed ) || null
          if ( seedNo !== null && seedNo <= config.playoffCutoff ) {
            seedValue = seedNo
          }
        }else if ( typeof entryVals.rank !== UNDEFINED && ( previousRank === null || previousRank != entryVals.rank ) ) {
          seedValue     = entryVals.rank
          previousRank  = entryVals.rank
        }
      }
      //highlight qualified teams only if the content is not sorted by any key
      if(config.highlightQualified && typeof selectn('params.sortKey',request) === 'undefined') {
        if (entryVals.qualified === 'Y') {
          prevQualified = 'Y'
        } else if (prevQualified === 'Y' && typeof entryVals.qualified === 'undefined') {
          prevQualified = 'N'
          entryVals.drawLine = true
        }
      }
      /* set the team up */
      entryVals[TEAM_COL_KEY] = {
        name :          displayName,
        abbreviation :  selectn('team.abbreviation', data[i]) || '',
        logo :    {
          href :        common.getTeamLogo( selectn( 'team', data[i] ) || {}, { w:50, h:50 } ),
          hasSprite :   hasSprite,
          teamId :      selectn( 'team.id', data[i] ) || 0,
          leagueId :    leagueId
        },
        isActive :      isActive,
        seedShow :      config.showPlayoffSeed || false,
        seedValue :     seedValue || '',
        seedSubShow :   '',
        clincher :      '',
        isCollege :     isCollege
      }

      const teamUrl = common.getTeamUrl( selectn( 'team', data[i] ) )
      if ( teamUrl && isActive ) {
        entryVals[TEAM_COL_KEY].link = common.buildLinkObject({
          href :      teamUrl,
          query :     request.query,
          tracking :  [ leagueId, 'standings', 'team' ],
          routerLink : false,
          dataRoute : dataRoute,
          edition : request.edition
        })
      }

      /* capture notes data */
      const noteDesc  = selectn( 'note.description', data[i] ) || ''
      const noteRank  = selectn( 'note.rank', data[i] ) || 0
      if ( noteDesc ) {
        notesData[noteRank] = noteDesc
      }

      entryData.push(entryVals)
    }

    return {
      entries : entryData || [],
      notes : standings.formatNotes(notesData, edition) || []
    }
  },

  /**
  * Takes an array of notes and condenses them down where the description is similar
  *
  * @param    {Array}   notesData     Array of notes from each entry in standings table
  * @return   {Array}               Condensed Notes
  **/
  formatNotes: function ( notesData = [], edition={} ) {

    const notesLen    = notesData.length || 0
    let prevNote    = ''
    let ranks       = []
    let formattedNotes  = []

    function formatNote( ranks = [], note = '' ) {

      let noteRank = 'Position ' + ranks[0]
      if ( ranks.length > 1 ) {
        noteRank = 'Positions ' + ranks[0] + '-' + ranks[ranks.length-1]
      }
      return noteRank + ': ' + note

    }

    for (let i = 0; i < notesLen; i++) {
      if ( notesData[i] ) {
        if ( prevNote && notesData[i] !== prevNote ) {
          formattedNotes.push( formatNote( ranks, prevNote ) )
          ranks = []
        }
        prevNote = notesData[i]
        ranks.push(i)
      }
    }

    if ( prevNote ) {
      formattedNotes.push( formatNote( ranks, prevNote ) )
    }

    if ( formattedNotes.length === 0 ) {
      const translatedNotes = translate(edition, 'standings.glossary.notes') || ''
      formattedNotes.push(translatedNotes)
    }

    return formattedNotes
  },

  /**
  * Searches the returned API data to look for the first valid standings data node
  * this is to be used to find default values about the API result i.e. season
  *
  * @param    {Object}  data      Standings API return value (unmolested)
  * @return   {Object}              First valid standings node
  **/
  getFirstStandings: function ( data ) {

    let firstStandings = {}

    if ( data.hasOwnProperty( 'standings' ) ) {
      const standingsData = selectn( 'standings', data ) || {}
      if (standings.hasStandingsData(standingsData)) {
        firstStandings = standingsData
      }
    }else if ( data.hasOwnProperty( 'children' ) ) {
      const childData = selectn( 'children', data ) || []
      const childLen  = childData.length || 0
      for ( let i = 0; i < childLen; i++ ) {
        const standingsData = standings.getFirstStandings( childData[i] )
        if (standings.hasStandingsData(standingsData)) {
          firstStandings = standingsData
          continue
        }
      }
    }

    return firstStandings
  },

  /**
  * Simple function to determine if the standings node has valid data
  *
  * @param  {Object}  data      Standings node from API
  * @return {Boolean}           returns true if has standings or false if not
  **/
  hasStandingsData: function ( data ) {
    const season  = selectn('season', data) || ''
    return season ? true : false
  },

  /**
  * Takes the first entry and checks to see if all the columns we want are there
  * and produces a definative col list based around the config and whats available
  *
  * @param  {Object}  config          Config values
  * @param  {Array}   standingsData   Standings node from API
  * @return {Object}                  Map of valid columns with key as column name
  **/
  getColumnFilter: function ( config = {}, standingsData = {} ) {

    /* build map of all content cats we are allowed in config */
    const categories  = config.categories || []
    const catLen    = categories.length
    let catMap      = {}
    let columnFilter  = {}

    for (let a = 0; a < catLen; a++) {
      const key = selectn('key', categories[a]) || ''
      if (key) {
        catMap[key] = {
          abbreviation :  '',
          sortable :    selectn('sortable', categories[a]) || 0,
          colgroup :    selectn('colgroup', categories[a]) || 0,
          description : '',
          colorize :    selectn('colorize', categories[a]) || false
        }
      }
    }

    /* build filtered list based on first set of data, this is to hide values if cols not in API */
    const stats     = selectn('entries[0].stats', standingsData) || []
    const statsLen    = stats.length
    for (let i = 0; i < statsLen; i++) {
      const key = selectn('type', stats[i]) || ''
      if (catMap[key]) {
        catMap[key].abbreviation  = selectn('abbreviation', stats[i]) || ''
        catMap[key].description   = selectn('description', stats[i]) || ''
        columnFilter[key] = catMap[key]
      }
    }

    return columnFilter
  },

  /**
  * Uses the config and the values stored in the validTypes to build the header values
  * and the links if required.
  *
  * @param    {Object}  request       Request Object made from 'editions', 'location' and 'params'
  * @param    {Object}  config        Config values
  * @param    {Object}  validTypes    Object returned from 'standings.getColumnFilter'
  * @param    {Object}  edition       Edition object
  * @param    {Object}  hrefConfig    Config object for create href function
  * @return   {Object}                Object of data attributes used to build table header
  **/
  getHeader: function ( request = {}, config = {}, validTypes = {}, edition, hrefConfig ) {

    const categories  = config.categories || []
    const catLen    = categories.length
    let header      = {}
    let glossary    = []
    let columns     = [ { key: TEAM_COL_KEY, colorize: false } ]

    /* set the team up */
    header[TEAM_COL_KEY] = {
      colgroup :  1,
      tooltip :   '',
      text :      'Team'
    }

    /* build an array of link objects in order. */
    for (let a = 0; a < catLen; a++) {
      const key = selectn('key', categories[a]) || ''
      if (validTypes[key]) {
        const { abbreviation = '', description = '' } = validTypes[key]

        header[key] = {
          colgroup :  validTypes[key].colgroup || 0,
          tooltip : validTypes[key].description || ''
        }

        if (typeof abbreviation == 'string' && typeof description == 'string') {
          if (abbreviation.toLowerCase() != description.toLowerCase()) {
            glossary.push({
              title : validTypes[key].abbreviation || '',
              description : validTypes[key].description || ''
            })
          }
        }

        if (validTypes[key].sortable) {

          let linkParams = Object.assign({}, request.params)
          delete linkParams['sortDir']
          linkParams.sortKey = key

          if (request.params.sortKey === key) {

            linkParams.sortDir = (selectn('parameters.dir',config) === 'asc') ? 'desc' : 'asc'

            if (linkParams.sortDir === selectn('oDefaultSortDirections.'+key,config)) {
              delete linkParams.sortDir
            }

          }

          header[key].link = common.buildLinkObject({
            edition,
            text :    validTypes[key].abbreviation,
            params :  linkParams,
            query :   request.query,
            tracking : [ request.params.league, 'standings', 'sort', helpers.descriptionToKey( validTypes[key].description ) ],
            hrefConfig
          })

        }else{
          header[key].text = validTypes[key].abbreviation || ''
        }

        columns.push({
          key :   key,
          colorize :  validTypes[key].colorize || false
        })
      }
    }

    return {
      data :    header,
      sortDir :   request.params.sortDir || '',
      sortCol :   request.params.sortKey || '',
      glossary,
      columns
    }
  },

  /**
  * Get a list of link objects for all seasons with at least 1 type that
  * has standings
  *
  * @param  {Object}    request    Request object
  * @param  {Object}    data       Standings API return value (unmolested)
  * @param  {Object}    hrefConfig config object for create href function
  * @param  {Object}    edition    edition object
  * @return {Array}                Array of link objects
  **/
  filterSeasons: function ( request, data, hrefConfig, edition ) {

    const seasonsRaw  = selectn('seasons', data) || []
    const seasonLen   = seasonsRaw.length || 0
    let seasons = []
    let language = edition && edition.language || 'en'
    const sport  = request.params.sport
    let pageTypeStandings = 'table'
    if(sport === 'rugby' && (language === 'es' || language === 'pt')) {
      pageTypeStandings = 'standings'
    }

    for (let i = 0; i < seasonLen; i++) {
      const season = seasonsRaw[i]
      /* need to actually filter by season type as well for other sports */
      const types     = selectn('types', season) || []
      const typeLen     = types.length
      let hasStanding   = 0

      for (let x = 0; x < typeLen; x++) {
        if (selectn('types['+x+'].hasStandings', season) === true) {
          hasStanding = selectn('types['+x+'].id', season) || 0
          continue
        }
      }

      if (hasStanding) {

        /* build out split years if required */

        const year = selectn('year', season) || ''
        let params = {
          pageType :  pageTypeStandings,
          sport :     sport,
          league :    request.params.league,
          season :    year,
          slug :      request.params.slug
        }
        let seasonText = standings.formatSeason( selectn('startDate', season),  selectn('endDate', season) )
        if (sport === 'rugby') {
          seasonText = season && season.year
        }

        if (hasStanding > 1) {
          params.seasontype = hasStanding
        }

        seasons.push( common.buildLinkObject({
          text :    seasonText,
          query :   request.query,
          params,
          tracking : [ request.params.league, 'standings', 'filter', 'year' ],
          hrefConfig,
          edition
        }))
      }
    }

    return seasons

  },

  /**
  * Take in dates as strings and extract years to return
  * formatted year YYYY-YY or YYYY
  *
  * @param  {string}  startDate   Start date ("2015-10-01T07:00Z")
  * @param    {string}  endDate     End date ("2015-10-01T07:00Z")
  * @return   {string}              Formatted string
  **/
  formatSeason: function ( startDate = '', endDate = '' ) {
    const start   = helpers.getYearFromDateString( startDate, 4 )
    const end     = helpers.getYearFromDateString( endDate, 4 )
    const endSub  = helpers.getYearFromDateString( endDate, 2 )
    if (start === end) {
      return start
    }
    return start+'-'+endSub
  },

  /**
  * Get a list of link objects for all valid leagues that have standings
  *
  * @param  {Object}  request    Request object
  * @param  {Object}  data       Return value from leagues API (unmolested)
  * @param  {Object}  hrefConfig config object for create href function
  * @param  {Object}  edition    edition object
  * @return {Array}             valid seasons
  **/
  filterLeagues: function ( request = {}, data = [], hrefConfig, edition) {

    const leagueLen   = data.length || 0
    let leagues     = []
    let sport = request.params.sport
    let pageTypeStandings = 'table'
    let language = edition && edition.language || 'en'

    for (let i = 0; i < leagueLen; i++) {
      const league = data[i]
      /* need to actually filter by season type as well for other sports */
      if (selectn('hasStandings', league) === true) {
        let leagueId  = selectn('midsizeName', league) || ''
        let slug    = ''
        if (sport === 'cricket') {
          leagueId  = selectn('id', league) || ''
          slug      = selectn('slug', league) || ''
        }
        if(sport === 'rugby' && (language === 'es' || language === 'pt')) {
          pageTypeStandings = 'standings'
        }
        leagues.push( common.buildLinkObject({
          text :    standings.getLeagueName(league) || '-',
          params :  {
            pageType :  pageTypeStandings,
            sport :     sport,
            league :    leagueId.toLowerCase(),
            slug :      slug
          },
          query :   request.query,
          tracking : [ leagueId, 'standings', 'filter', 'league' ],
          hrefConfig,
          edition
        }))
      }
    }

    return leagues

  },

  /**
  * Construct the league title from league name, season and type
  *
  * @param  {String}  leagueName    Current league name
  * @param  {String}  seasonDesc    Current league name
  * @param  {Object}  params        Router params
  * @param  {Object}  edition        Edition params
  * @return {String}                league title
  **/
  getLeagueTitle: function ( leagueName, seasonDesc, params, edition ) {

    let { sport, pageType } = params
    let leagueTitle = ''
    /*
    * capitalize pagetype and prefix with '-'
    * @Example : - Table
    * @Example : - Standings
    */
    if (typeof pageType === 'string') {
      pageType = pageType.replace(/^([a-z])/i, (char) => { return char.toUpperCase() })
    } else {
      pageType = ''
    }

    if ( params.sport === 'cricket' ) {
      leagueTitle = leagueName + ' ' + seasonDesc
    } else {
      leagueTitle = leagueName + ' ' + pageType + ' - ' + seasonDesc
    }

    //concat league title and pagetype
    if (sport === 'rugby' && (pageType === 'standings' || pageType === 'Table')) {
      const transObj = {
        key : 'rugby.standings.leaguetitle',
        league : leagueName,
        season : seasonDesc
      }
      leagueTitle = translate(edition, transObj) || ''
    } else {
      leagueTitle = leagueName + ' ' + pageType + ' - ' + seasonDesc
    }
    return leagueTitle

  },
  getLeagueLink: function ( params={}, leagues=[]) {
    const leagueStr = params.league ? params.league.toString() : '0'
    let items = Object.values(leagues) || []
    const itemsLen = items.length || 0
    for (let i=0; i < itemsLen; i++) {
      if (items[i].league === leagueStr) {
        return common.createHref(params) || items[i].href || ''       // Call common.createHref to create the canonical link again, with season param (CTOI-31014)
      }
    }
  },

  /**
  * Gets the league name from the data and formats accordingly
  *
  * @param  {Object}  data      League Object
  * @return {String}                  league title
  **/
  getLeagueName: function ( data ) {
    return standings.removeYearfromName( selectn('name', data) || '').trim()
  },

  /**
   * @param  {String} currLeague current League
   * @param  {Array} leagues league array
   * @return {[type]} leagueId league id
   */
  getLeagueId: function ( currLeague = '' , leagues = []) {
    let leagueId
    for (var i = leagues.length - 1; i >=0; i --) {
      let league = leagues[i] || {}
      let leagueName = league.name || ''
      if (leagueName.trim() === currLeague) {
        leagueId = league.id
      }
    }
    return leagueId || ''
  },

  /**
  * Takes the league name and looks for years patters to remove from the name
  *
  * @param  {String}  name      League name
  * @return {String}                  formatted string
  **/
  removeYearfromName: function ( name ) {
    let retName = name
    retName = retName.replace(/ \d{4}$/,'')
    retName = retName.replace(/ \d{4}\/\d{2}$/,'')
    retName = retName.replace(/^\d{4}\/\d{4} /,'')
    retName = retName.replace(/^\d{4}\/\d{2} /,'')
    return retName
  },

  /**
  * finds the rules string in the API seperates by \n then joins back together
  * wrapped in HTML tags
  * NOTE this should be passed as an array to the react elements to deal with
  *
  * @param  {Object}  data  Return value from standings API (unmolested)
  * @return {String}        HTML string of rules
  **/
  getRules: function (data) {
    const rules = selectn('rules.Story', data)
    let rulesParsed = ''

    // return empty string if rules are not string
    if (typeof rules !== 'string') {
      return rulesParsed
    }

    rulesParsed = rules
      .split('\n\n')
      .map((text)=>{ return `<p>${text}</p>` })
      .join('')

    return rulesParsed
  },

  /**
  * Finds or defaults the year/season from the season node
  *
  * @param    {Object}  config      Config values
  * @param    {Object}  data        Season Node from Standings API
  * @return   {String}                Object of filtered data
  **/
  getStandingsSeason: function ( config = {}, data = {} ) {
    return selectn('season',data) || ''
  },

  /**
  * Finds or defaults the season type from the season node
  *
  * @param    {Object}  config      Config values
  * @param    {Object}  data        Season Node from Standings API
  * @return   {String}              Object of filtered data
  **/
  getStandingsSeasonType: function ( config = {}, data = {} ) {
    return selectn('seasonType',data) || config.parameters.seasontype || 0
  },

  /**
  * Finds the season node for a defined year from the seasons Array in the standings data
  *
  * @param    {String}  season      Season as year e.g. 2016
  * @param    {Object}  data        Return value from standings API (unmolested)
  * @return   {String}                Object of filtered data
  **/
  getCurrentSeasonData: function ( season = '', data = {} ) {

    const seasonsRaw  = selectn('seasons', data) || []
    const seasonLen   = seasonsRaw.length || 0

    if (!season && seasonLen) {
      // no current season is available because the latest season has no data
      // to prevent the site from displaying empty season dropdown selections, just pick
      // the first season in the season list
      return seasonsRaw[0]
    }

    for (let i = 0; i < seasonLen; i++) {
      const year    = selectn('year', seasonsRaw[i]) || ''
      if (year === season) {
        return seasonsRaw[i]
      }
    }

    return {}
  },

  /**
  * Builds an object that can be passed straight to 'Dropdown' component i.e. {...values}
  *
  * @param    {Object}  request     Request object
  * @param    {Object}  data        Current season node from seasons array
  * @param    {String}  seasonType      Current season type
  * @param    {Object}  edition     edition object
  * @return   {String}                Object of filtered data
  **/
  getSeasonTypes: function ( request = {}, data = {}, seasonType = 1, edition ) {

    const types   = selectn('types', data) || []
    const typeLen   = types.length || 0
    let items   = []
    let selected  = ''

    for (let i = 0; i < typeLen; i++) {
      const typeData = types[i]

      if (selectn('hasStandings', typeData) === true) {

        const name = selectn('name', typeData) || ''
        const type = selectn('id', typeData) || 1

        if ( !selected || type == seasonType ) {
          selected = name
        }

        let params = {
          pageType :    'table',
          sport :     request.params.sport,
          league :    request.params.league,
          season :    request.params.season,
          slug :      request.params.slug,
          seasontype :  type
        }

        items.push( common.buildLinkObject({
          text :    name,
          query :   request.query,
          tracking : [ request.params.league, 'standings', 'filter', helpers.descriptionToKey( name ) ] ,
          params,
          edition
        }))

      }
    }

    return {
      selected : selected || '',
      items : items || []
    }
  }

}

export default standings



// WEBPACK FOOTER //
// ../app/lib/transform/parsers/standings.js